import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { apiClient } from '../api/apiConfig';
import API_BASE from '../apiBase';
import { COLORS, SPACING, TYPOGRAPHY, RADIUS } from '../design/tokens';
import { Panel, Button, Input, Spinner } from '../design/primitives';
import {
  PlusIcon,
  PaperAirplaneIcon,
  TrashIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  WrenchScrewdriverIcon,
  CheckIcon,
  XMarkIcon,
  PhotoIcon,
  BoltIcon,
  CommandLineIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

interface Conversation {
  id: number;
  title: string;
  updated_at: string;
  message_count: number;
}

interface ToolCall {
  tool_name: string;
  tool_input: Record<string, unknown>;
  tool_use_id: string;
  needs_confirmation?: boolean;
}

interface ToolResult {
  tool_name: string;
  result: string;
  tool_use_id: string;
}

interface ImageAttachment {
  data: string; // base64
  media_type: string; // e.g. image/png
  preview_url: string; // for local display
}

interface Message {
  id: number;
  role: 'user' | 'assistant' | 'tool';
  content: string;
  images?: ImageAttachment[];
  tool_calls: ToolCall[];
  tool_results: ToolResult[];
  created_at: string;
  model?: string;
}

interface ActiveTool {
  name: string;
  id: string;
  done: boolean;
}

const BUILD_VERSION = '2026-02-08T10:00:00';

// Markdown component overrides styled with design tokens
const markdownComponents: Record<string, React.FC<Record<string, unknown>>> = {
  code: ({ inline, children, ...props }: { inline?: boolean; children?: React.ReactNode; [key: string]: unknown }) => {
    if (inline) {
      return (
        <code
          style={{
            backgroundColor: 'rgba(0,0,0,0.08)',
            padding: '2px 6px',
            borderRadius: 4,
            fontFamily: 'monospace',
            fontSize: '0.9em',
          }}
          {...props}
        >
          {children}
        </code>
      );
    }
    return (
      <code
        style={{
          fontFamily: 'monospace',
          fontSize: '0.9em',
        }}
        {...props}
      >
        {children}
      </code>
    );
  },
  pre: ({ children, ...props }: { children?: React.ReactNode; [key: string]: unknown }) => (
    <pre
      style={{
        backgroundColor: 'rgba(0,0,0,0.06)',
        padding: SPACING[12],
        borderRadius: RADIUS,
        overflowX: 'auto',
        fontFamily: 'monospace',
        fontSize: '0.85em',
        lineHeight: '1.5',
        margin: `${SPACING[8]} 0`,
      }}
      {...props}
    >
      {children}
    </pre>
  ),
  table: ({ children, ...props }: { children?: React.ReactNode; [key: string]: unknown }) => (
    <table
      style={{
        borderCollapse: 'collapse',
        width: '100%',
        margin: `${SPACING[8]} 0`,
        fontSize: TYPOGRAPHY.body.sizes.small,
      }}
      {...props}
    >
      {children}
    </table>
  ),
  th: ({ children, ...props }: { children?: React.ReactNode; [key: string]: unknown }) => (
    <th
      style={{
        border: `1px solid ${COLORS.border.default}`,
        padding: `${SPACING[4]} ${SPACING[8]}`,
        backgroundColor: 'rgba(0,0,0,0.04)',
        textAlign: 'left',
        fontWeight: 600,
      }}
      {...props}
    >
      {children}
    </th>
  ),
  td: ({ children, ...props }: { children?: React.ReactNode; [key: string]: unknown }) => (
    <td
      style={{
        border: `1px solid ${COLORS.border.default}`,
        padding: `${SPACING[4]} ${SPACING[8]}`,
      }}
      {...props}
    >
      {children}
    </td>
  ),
  a: ({ children, href, ...props }: { children?: React.ReactNode; href?: string; [key: string]: unknown }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      style={{ color: COLORS.accent.default, textDecoration: 'underline' }}
      {...props}
    >
      {children}
    </a>
  ),
  ul: ({ children, ...props }: { children?: React.ReactNode; [key: string]: unknown }) => (
    <ul style={{ paddingLeft: SPACING[24], margin: `${SPACING[4]} 0` }} {...props}>
      {children}
    </ul>
  ),
  ol: ({ children, ...props }: { children?: React.ReactNode; [key: string]: unknown }) => (
    <ol style={{ paddingLeft: SPACING[24], margin: `${SPACING[4]} 0` }} {...props}>
      {children}
    </ol>
  ),
  li: ({ children, ...props }: { children?: React.ReactNode; [key: string]: unknown }) => (
    <li style={{ marginBottom: SPACING[4] }} {...props}>
      {children}
    </li>
  ),
  blockquote: ({ children, ...props }: { children?: React.ReactNode; [key: string]: unknown }) => (
    <blockquote
      style={{
        borderLeft: `3px solid ${COLORS.accent.default}`,
        paddingLeft: SPACING[12],
        margin: `${SPACING[8]} 0`,
        color: COLORS.text.secondary,
      }}
      {...props}
    >
      {children}
    </blockquote>
  ),
  p: ({ children, ...props }: { children?: React.ReactNode; [key: string]: unknown }) => (
    <p style={{ margin: `${SPACING[4]} 0` }} {...props}>
      {children}
    </p>
  ),
};

const ChatPage: React.FC = () => {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [activeConversation, setActiveConversation] = useState<number | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [images, setImages] = useState<ImageAttachment[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingConversations, setLoadingConversations] = useState(true);
  const [pendingConfirmations, setPendingConfirmations] = useState<ToolCall[]>([]);
  const [agentMode, setAgentMode] = useState(false);
  const [codeMode, setCodeMode] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');
  const [activeTools, setActiveTools] = useState<ActiveTool[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingModel, setStreamingModel] = useState('');
  const [verification, setVerification] = useState<{verified: boolean; issues: string[]; summary: string} | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [usageStats, setUsageStats] = useState<{totals: {cost: string; api_calls: number}; today: {cost: string; api_calls: number}} | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const inputAreaRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef('');

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, streamingContent]);

  useEffect(() => {
    fetchConversations();
  }, []);

  // Fetch API usage stats
  const fetchUsageStats = useCallback(async () => {
    try {
      const now = new Date();
      const start = new Date(now);
      start.setDate(start.getDate() - 30);
      const res = await apiClient.get('/api/chat/usage/', {
        params: {
          start: start.toISOString().split('T')[0],
          end: now.toISOString().split('T')[0],
          period: 'day',
        },
      });
      setUsageStats(res.data);
    } catch (e) {
      // Silently ignore — stats are non-critical
    }
  }, []);

  useEffect(() => {
    fetchUsageStats();
  }, [messages]);

  const fileToBase64 = (file: File): Promise<ImageAttachment> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        const base64 = result.split(',')[1];
        const media_type = file.type || 'image/png';
        resolve({
          data: base64,
          media_type,
          preview_url: result,
        });
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const addImageFiles = useCallback(async (files: File[]) => {
    const imageFiles = files.filter(f => f.type.startsWith('image/'));
    if (imageFiles.length === 0) return;
    const newImages = await Promise.all(imageFiles.map(fileToBase64));
    setImages(prev => [...prev, ...newImages]);
  }, []);

  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    const imageFiles: File[] = [];
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.startsWith('image/')) {
        const file = items[i].getAsFile();
        if (file) imageFiles.push(file);
      }
    }
    if (imageFiles.length > 0) {
      e.preventDefault();
      addImageFiles(imageFiles);
    }
  }, [addImageFiles]);

  const handleFilePick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    addImageFiles(files);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeImage = (index: number) => {
    setImages(prev => prev.filter((_, i) => i !== index));
  };

  const fetchConversations = async () => {
    try {
      const res = await apiClient.get('/api/chat/conversations/');
      setConversations(res.data);
    } catch (err) {
      console.error('Failed to fetch conversations', err);
    } finally {
      setLoadingConversations(false);
    }
  };

  const createConversation = async () => {
    try {
      const res = await apiClient.post('/api/chat/conversations/', { title: 'New Chat' });
      setConversations(prev => [res.data, ...prev]);
      setActiveConversation(res.data.id);
      setMessages([]);
      setPendingConfirmations([]);
    } catch (err) {
      console.error('Failed to create conversation', err);
    }
  };

  const deleteConversation = async (id: number) => {
    try {
      await apiClient.delete(`/api/chat/conversations/${id}/`);
      setConversations(prev => prev.filter(c => c.id !== id));
      if (activeConversation === id) {
        setActiveConversation(null);
        setMessages([]);
      }
    } catch (err) {
      console.error('Failed to delete conversation', err);
    }
  };

  const loadMessages = async (conversationId: number) => {
    setActiveConversation(conversationId);
    setPendingConfirmations([]);
    try {
      const res = await apiClient.get(`/api/chat/conversations/${conversationId}/messages/`);
      setMessages(res.data);

      const msgs = res.data as Message[];
      const lastAssistantMsg = [...msgs].reverse().find(m => m.role === 'assistant');
      if (lastAssistantMsg?.tool_calls?.length && lastAssistantMsg?.tool_results?.length) {
        const pendingTools = lastAssistantMsg.tool_calls.filter((tc: ToolCall) => {
          const result = lastAssistantMsg.tool_results.find((tr: ToolResult) => tr.tool_use_id === tc.tool_use_id);
          return result && result.result === 'awaiting_confirmation';
        });
        if (pendingTools.length > 0) {
          setPendingConfirmations(pendingTools.map((tc: ToolCall) => ({ ...tc, needs_confirmation: true })));
        }
      }
    } catch (err) {
      console.error('Failed to load messages', err);
    }
  };

  const sendMessage = async (confirmedToolIds?: Record<string, boolean>) => {
    const content = input.trim();
    const currentImages = [...images];
    if (!content && !confirmedToolIds && currentImages.length === 0) return;

    let conversationId = activeConversation;
    if (!conversationId) {
      try {
        const res = await apiClient.post('/api/chat/conversations/', { title: 'New Chat' });
        conversationId = res.data.id;
        setConversations(prev => [res.data, ...prev]);
        setActiveConversation(conversationId);
        setMessages([]);
        setPendingConfirmations([]);
      } catch (err) {
        console.error('Failed to create conversation', err);
        return;
      }
    }

    if (content || currentImages.length > 0) {
      setMessages(prev => [...prev, {
        id: Date.now(),
        role: 'user',
        content,
        images: currentImages,
        tool_calls: [],
        tool_results: [],
        created_at: new Date().toISOString(),
      }]);
      setInput('');
      setImages([]);
    }

    setLoading(true);
    setIsStreaming(true);
    setPendingConfirmations([]);
    setStreamingContent('');
    setStreamingModel('');
    setActiveTools([]);
    setVerification(null);
    setIsVerifying(false);
    contentRef.current = '';

    try {
      const token = localStorage.getItem('floos_access_token');
      const payload: Record<string, unknown> = {
        content: content || '',
        confirmed_tool_ids: confirmedToolIds || {},
        agent_mode: agentMode,
        code_mode: codeMode,
      };
      if (currentImages.length > 0) {
        payload.images = currentImages.map(img => ({
          data: img.data,
          media_type: img.media_type,
        }));
      }

      const response = await fetch(`${API_BASE}/api/chat/conversations/${conversationId}/stream/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const reader = response.body!.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let finalToolCalls: ToolCall[] = [];
      let finalToolResults: ToolResult[] = [];
      let finalContent = '';
      let finalModel = '';
      let gotDone = false;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // Parse SSE events from buffer
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const jsonStr = line.slice(6).trim();
          if (!jsonStr) continue;

          try {
            const parsed = JSON.parse(jsonStr);
            const event = parsed.event;

            if (event === 'model') {
              finalModel = parsed.model || '';
              setStreamingModel(finalModel);
            } else if (event === 'text_delta') {
              contentRef.current += parsed.content || '';
              setStreamingContent(contentRef.current);
            } else if (event === 'tool_start') {
              setActiveTools(prev => [...prev, { name: parsed.tool_name, id: parsed.tool_use_id, done: false }]);
            } else if (event === 'tool_result') {
              setActiveTools(prev => prev.map(t =>
                t.id === parsed.tool_use_id ? { ...t, done: true } : t
              ));
            } else if (event === 'verification_start') {
              setIsVerifying(true);
            } else if (event === 'verification') {
              setVerification(parsed);
              setIsVerifying(false);
            } else if (event === 'confirmation_needed') {
              finalContent = parsed.content || contentRef.current;
              finalToolCalls = parsed.tool_calls || [];
              finalToolResults = parsed.tool_results || [];
              finalModel = parsed.model || finalModel;
              gotDone = true;

              // Add the assistant message so far
              setMessages(prev => [...prev, {
                id: Date.now(),
                role: 'assistant',
                content: finalContent,
                tool_calls: finalToolCalls,
                tool_results: finalToolResults,
                created_at: new Date().toISOString(),
                model: finalModel,
              }]);
              setPendingConfirmations(parsed.pending_tool_calls || []);
            } else if (event === 'done') {
              finalContent = parsed.content || contentRef.current;
              finalToolCalls = parsed.tool_calls || [];
              finalToolResults = parsed.tool_results || [];
              finalModel = parsed.model || finalModel;
              gotDone = true;

              if (parsed.verification) setVerification(parsed.verification);

              setMessages(prev => [...prev, {
                id: Date.now(),
                role: 'assistant',
                content: finalContent,
                tool_calls: finalToolCalls,
                tool_results: finalToolResults,
                created_at: new Date().toISOString(),
                model: finalModel,
              }]);
            }
          } catch {
            // Ignore parse errors for incomplete JSON
          }
        }
      }

      // If stream ended without a done/confirmation event, add whatever we have
      if (!gotDone && contentRef.current) {
        setMessages(prev => [...prev, {
          id: Date.now(),
          role: 'assistant',
          content: contentRef.current,
          tool_calls: [],
          tool_results: [],
          created_at: new Date().toISOString(),
          model: finalModel,
        }]);
      }

      fetchConversations();
    } catch (err) {
      console.error('Failed to send message', err);
      setMessages(prev => [...prev, {
        id: Date.now(),
        role: 'assistant',
        content: 'Sorry, there was an error processing your message. Please try again.',
        tool_calls: [],
        tool_results: [],
        created_at: new Date().toISOString(),
      }]);
    } finally {
      setLoading(false);
      setIsStreaming(false);
      setStreamingContent('');
      setActiveTools([]);
      contentRef.current = '';
    }
  };

  const handleConfirm = (toolCall: ToolCall) => {
    const confirmed: Record<string, boolean> = {};
    confirmed[toolCall.tool_use_id] = true;
    sendMessage(confirmed);
  };

  const handleConfirmAll = () => {
    const confirmed: Record<string, boolean> = {};
    pendingConfirmations.forEach(tc => {
      confirmed[tc.tool_use_id] = true;
    });
    sendMessage(confirmed);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div style={{ backgroundColor: COLORS.bg.base, height: '100%', overflow: 'hidden' }}>
      <div style={{ display: 'flex', height: '100%', gap: 0 }}>
        {/* Sidebar */}
        <div style={{
          width: 260,
          borderRight: `1px solid ${COLORS.border.default}`,
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: COLORS.bg.panel,
          flexShrink: 0,
        }}>
          <div style={{ padding: SPACING[12] }}>
            <Button onClick={createConversation} style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: SPACING[8] }}>
              <PlusIcon style={{ width: 16, height: 16 }} />
              New Chat
            </Button>
          </div>
          <div style={{ flex: 1, overflowY: 'auto', padding: `0 ${SPACING[8]}` }}>
            {loadingConversations ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: SPACING[24] }}>
                <Spinner />
              </div>
            ) : conversations.map(conv => (
              <div
                key={conv.id}
                onClick={() => loadMessages(conv.id)}
                style={{
                  padding: `${SPACING[8]} ${SPACING[12]}`,
                  borderRadius: RADIUS,
                  cursor: 'pointer',
                  backgroundColor: activeConversation === conv.id ? 'rgba(59, 130, 246, 0.1)' : 'transparent',
                  color: activeConversation === conv.id ? COLORS.accent.default : COLORS.text.secondary,
                  fontFamily: TYPOGRAPHY.body.fontFamily,
                  fontSize: TYPOGRAPHY.body.sizes.default,
                  marginBottom: SPACING[4],
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                  {conv.title}
                </span>
                <button
                  onClick={(e) => { e.stopPropagation(); deleteConversation(conv.id); }}
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer', padding: SPACING[4],
                    opacity: 0.5, flexShrink: 0,
                  }}
                >
                  <TrashIcon style={{ width: 14, height: 14, color: COLORS.text.tertiary }} />
                </button>
              </div>
            ))}
          </div>
          {/* API Cost Summary */}
          {usageStats && (
            <div style={{
              padding: `${SPACING[8]} ${SPACING[12]}`,
              fontSize: '11px',
              color: COLORS.text.secondary,
              borderTop: `1px solid ${COLORS.border.default}`,
              lineHeight: 1.5,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Today</span>
                <span>${parseFloat(usageStats.today.cost).toFixed(4)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>30-day</span>
                <span>${parseFloat(usageStats.totals.cost).toFixed(2)} ({usageStats.totals.api_calls} calls)</span>
              </div>
            </div>
          )}
          {/* Version indicator */}
          <div style={{
            padding: SPACING[8],
            fontSize: '10px',
            color: COLORS.text.tertiary,
            borderTop: `1px solid ${COLORS.border.default}`,
            textAlign: 'center',
          }}>
            v{BUILD_VERSION}
          </div>
        </div>

        {/* Chat area */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', backgroundColor: COLORS.bg.base }}>
          {!activeConversation ? (
            <div style={{
              flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexDirection: 'column', gap: SPACING[16], color: COLORS.text.tertiary,
            }}>
              <WrenchScrewdriverIcon style={{ width: 48, height: 48 }} />
              <span style={{ fontFamily: TYPOGRAPHY.body.fontFamily, fontSize: TYPOGRAPHY.size.lg }}>
                Select a conversation or start a new chat
              </span>
              <Button onClick={createConversation}>New Chat</Button>
            </div>
          ) : (
            <>
              {/* Messages */}
              <div style={{ flex: 1, overflowY: 'auto', padding: SPACING[24] }}>
                {messages.map((msg, idx) => {
                  const isLastAssistant = msg.role === 'assistant' && !messages.slice(idx + 1).some(m => m.role === 'assistant');
                  return (
                    <MessageBubble
                      key={msg.id}
                      message={msg}
                      verification={isLastAssistant ? verification : null}
                      isVerifying={isLastAssistant ? isVerifying : false}
                    />
                  );
                })}
                {/* Streaming message */}
                {isStreaming && streamingContent && (
                  <div style={{
                    display: 'flex',
                    justifyContent: 'flex-start',
                    marginBottom: SPACING[12],
                    maxWidth: 800,
                    margin: `0 auto ${SPACING[12]}`,
                  }}>
                    <div style={{
                      maxWidth: '80%',
                      padding: `${SPACING[12]} ${SPACING[16]}`,
                      borderRadius: RADIUS,
                      backgroundColor: COLORS.bg.panel,
                      color: COLORS.text.primary,
                      fontFamily: TYPOGRAPHY.body.fontFamily,
                      fontSize: TYPOGRAPHY.body.sizes.default,
                      lineHeight: '1.5',
                      border: `1px solid ${COLORS.border.default}`,
                      wordBreak: 'break-word',
                      position: 'relative',
                    }}>
                      {streamingModel && (() => {
                        const ml = getModelLabel(streamingModel);
                        return ml ? (
                          <span style={{
                            position: 'absolute',
                            top: -8,
                            right: 8,
                            backgroundColor: ml.color,
                            color: '#fff',
                            padding: `2px ${SPACING[8]}`,
                            borderRadius: RADIUS,
                            fontSize: TYPOGRAPHY.body.sizes.tiny,
                            fontWeight: 600,
                          }}>
                            {ml.name}
                          </span>
                        ) : null;
                      })()}
                      <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                        {streamingContent}
                      </ReactMarkdown>
                    </div>
                  </div>
                )}
                {/* Loading / streaming indicator */}
                {loading && !streamingContent && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[8], padding: SPACING[12], color: COLORS.text.tertiary }}>
                    <Spinner />
                    <span style={{ fontFamily: TYPOGRAPHY.body.fontFamily, fontSize: TYPOGRAPHY.body.sizes.default }}>Thinking...</span>
                  </div>
                )}
                {pendingConfirmations.length > 0 && (
                  <div style={{ padding: SPACING[12] }}>
                    {pendingConfirmations.length > 1 ? (
                      <BatchConfirmationCard
                        toolCalls={pendingConfirmations}
                        onConfirmAll={handleConfirmAll}
                        onReject={() => setPendingConfirmations([])}
                      />
                    ) : (
                      pendingConfirmations.map(tc => (
                        <ConfirmationCard key={tc.tool_use_id} toolCall={tc} onConfirm={handleConfirm} onReject={() => setPendingConfirmations([])} />
                      ))
                    )}
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Agent progress panel */}
              {isStreaming && activeTools.length > 0 && (
                <div style={{
                  padding: `${SPACING[8]} ${SPACING[16]}`,
                  borderTop: `1px solid ${COLORS.border.default}`,
                  backgroundColor: 'rgba(59, 130, 246, 0.05)',
                  fontFamily: TYPOGRAPHY.body.fontFamily,
                  fontSize: TYPOGRAPHY.body.sizes.small,
                }}>
                  <div style={{ maxWidth: 800, margin: '0 auto' }}>
                    <div style={{ fontWeight: 600, marginBottom: SPACING[4], color: COLORS.text.secondary }}>
                      Agent working...
                    </div>
                    {activeTools.map(tool => (
                      <div key={tool.id} style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: SPACING[8],
                        padding: `2px 0`,
                        color: tool.done ? COLORS.status.success : COLORS.text.secondary,
                      }}>
                        <span style={{ width: 16, textAlign: 'center' }}>
                          {tool.done ? '\u2713' : '\u21BB'}
                        </span>
                        <span>{tool.name.replace(/_/g, ' ')}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Input area */}
              <div
                ref={inputAreaRef}
                onPaste={handlePaste}
                style={{
                  padding: SPACING[16],
                  borderTop: `1px solid ${COLORS.border.default}`,
                  backgroundColor: COLORS.bg.panel,
                }}
              >
                {/* Image previews */}
                {images.length > 0 && (
                  <div style={{ display: 'flex', gap: SPACING[8], marginBottom: SPACING[8], maxWidth: 800, margin: `0 auto ${SPACING[8]}`, flexWrap: 'wrap' }}>
                    {images.map((img, i) => (
                      <div key={i} style={{ position: 'relative', display: 'inline-block' }}>
                        <img
                          src={img.preview_url}
                          alt={`Attachment ${i + 1}`}
                          style={{ height: 64, borderRadius: RADIUS, border: `1px solid ${COLORS.border.default}` }}
                        />
                        <button
                          onClick={() => removeImage(i)}
                          style={{
                            position: 'absolute', top: -6, right: -6,
                            width: 18, height: 18, borderRadius: '50%',
                            backgroundColor: COLORS.status.error, border: 'none',
                            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                            padding: 0,
                          }}
                        >
                          <XMarkIcon style={{ width: 12, height: 12, color: '#fff' }} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <div style={{ display: 'flex', gap: SPACING[8], maxWidth: 800, margin: '0 auto', alignItems: 'center' }}>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleFilePick}
                    style={{ display: 'none' }}
                  />
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    title="Attach image"
                    style={{
                      background: 'none', border: `1px solid ${COLORS.border.default}`,
                      borderRadius: RADIUS, cursor: 'pointer', padding: SPACING[8],
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <PhotoIcon style={{ width: 18, height: 18, color: COLORS.text.secondary }} />
                  </button>
                  <button
                    onClick={() => { setAgentMode(!agentMode); if (!agentMode) setCodeMode(false); }}
                    title={agentMode ? 'Agent mode ON: auto-execute write tools' : 'Agent mode OFF: confirm write tools'}
                    style={{
                      background: agentMode ? COLORS.accent.default : 'none',
                      border: agentMode ? 'none' : `1px solid ${COLORS.border.default}`,
                      borderRadius: RADIUS,
                      cursor: 'pointer',
                      padding: SPACING[8],
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <BoltIcon style={{ width: 18, height: 18, color: agentMode ? '#fff' : COLORS.text.secondary }} />
                  </button>
                  <button
                    onClick={() => { setCodeMode(!codeMode); if (!codeMode) setAgentMode(false); }}
                    title={codeMode ? 'Code mode ON: routes through Claude Code (Max plan)' : 'Code mode OFF: uses direct API'}
                    style={{
                      background: codeMode ? '#F59E0B' : 'none',
                      border: codeMode ? 'none' : `1px solid ${COLORS.border.default}`,
                      borderRadius: RADIUS,
                      cursor: 'pointer',
                      padding: SPACING[8],
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <CommandLineIcon style={{ width: 18, height: 18, color: codeMode ? '#fff' : COLORS.text.secondary }} />
                  </button>
                  <Input
                    value={input}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={images.length > 0 ? "Add a message about the image..." : "Ask about your data... (paste images with Ctrl+V)"}
                    disabled={loading}
                    style={{ flex: 1 }}
                  />
                  <Button onClick={() => sendMessage()} disabled={loading || (!input.trim() && images.length === 0)}>
                    <PaperAirplaneIcon style={{ width: 16, height: 16 }} />
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const getModelLabel = (model?: string): { name: string; color: string } | null => {
  if (!model) return null;
  if (model.startsWith('claude-code')) return { name: 'Code', color: '#F59E0B' };
  if (model.includes('haiku')) return { name: 'Haiku', color: '#10B981' };
  if (model.includes('sonnet')) return { name: 'Sonnet', color: '#6366F1' };
  if (model.includes('opus')) return { name: 'Opus', color: '#F59E0B' };
  return null;
};

const MessageBubble: React.FC<{
  message: Message;
  verification?: {verified: boolean; issues: string[]; summary: string} | null;
  isVerifying?: boolean;
}> = ({ message, verification = null, isVerifying = false }) => {
  const isUser = message.role === 'user';
  const modelLabel = getModelLabel(message.model);

  return (
    <div style={{
      display: 'flex',
      justifyContent: isUser ? 'flex-end' : 'flex-start',
      marginBottom: SPACING[12],
      maxWidth: 800,
      margin: `0 auto ${SPACING[12]}`,
    }}>
      <div style={{
        maxWidth: '80%',
        padding: `${SPACING[12]} ${SPACING[16]}`,
        borderRadius: RADIUS,
        backgroundColor: isUser ? COLORS.accent.default : COLORS.bg.panel,
        color: isUser ? '#FFFFFF' : COLORS.text.primary,
        fontFamily: TYPOGRAPHY.body.fontFamily,
        fontSize: TYPOGRAPHY.body.sizes.default,
        lineHeight: '1.5',
        border: isUser ? 'none' : `1px solid ${COLORS.border.default}`,
        whiteSpace: isUser ? 'pre-wrap' : undefined,
        wordBreak: 'break-word',
        position: 'relative',
      }}>
        {/* Model badge */}
        {modelLabel && (
          <span style={{
            position: 'absolute',
            top: -8,
            right: 8,
            backgroundColor: modelLabel.color,
            color: '#fff',
            padding: `2px ${SPACING[8]}`,
            borderRadius: RADIUS,
            fontSize: TYPOGRAPHY.body.sizes.tiny,
            fontWeight: 600,
          }}>
            {modelLabel.name}
          </span>
        )}
        {/* Show attached images */}
        {message.images && message.images.length > 0 && (
          <div style={{ marginBottom: SPACING[8], display: 'flex', gap: SPACING[8], flexWrap: 'wrap' }}>
            {message.images.map((img, i) => (
              <img
                key={i}
                src={img.preview_url || `data:${img.media_type};base64,${img.data}`}
                alt={`Attachment ${i + 1}`}
                style={{ maxWidth: 300, maxHeight: 200, borderRadius: RADIUS, border: '1px solid rgba(255,255,255,0.2)' }}
              />
            ))}
          </div>
        )}
        {isUser ? (
          message.content
        ) : (
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
            {message.content}
          </ReactMarkdown>
        )}
        {message.tool_calls && message.tool_calls.length > 0 && (
          <ToolCallsDisplay toolCalls={message.tool_calls} toolResults={message.tool_results} />
        )}
        {/* Verification badge */}
        {!isUser && (verification || isVerifying) && (
          <div style={{
            marginTop: SPACING[8],
            display: 'flex',
            alignItems: 'center',
            gap: SPACING[4],
            fontSize: TYPOGRAPHY.size.xs,
          }}>
            {isVerifying && (
              <span style={{ color: COLORS.text.tertiary, display: 'flex', alignItems: 'center', gap: 4 }}>
                <Spinner /> Verifying...
              </span>
            )}
            {verification && !isVerifying && (verification.verified ? (
              <span style={{ color: COLORS.status.success, display: 'flex', alignItems: 'center', gap: 4 }}>
                <ShieldCheckIcon style={{ width: 14, height: 14 }} />
                GPT verified
              </span>
            ) : (
              <details style={{ color: COLORS.status.warning }}>
                <summary style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
                  <ExclamationTriangleIcon style={{ width: 14, height: 14 }} />
                  {verification.issues.length} issue{verification.issues.length !== 1 ? 's' : ''} found
                </summary>
                <ul style={{ margin: `${SPACING[4]}px 0 0 ${SPACING[16]}px`, padding: 0 }}>
                  {verification.issues.map((issue, i) => (
                    <li key={i} style={{ marginBottom: SPACING[4] }}>{issue}</li>
                  ))}
                </ul>
              </details>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const ToolCallsDisplay: React.FC<{ toolCalls: ToolCall[]; toolResults: ToolResult[] }> = ({ toolCalls, toolResults }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div style={{ marginTop: SPACING[8] }}>
      <button
        onClick={() => setExpanded(!expanded)}
        style={{
          background: 'none', border: 'none', cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: SPACING[4],
          color: COLORS.text.tertiary, fontFamily: TYPOGRAPHY.body.fontFamily,
          fontSize: TYPOGRAPHY.body.sizes.small, padding: 0,
        }}
      >
        {expanded ? <ChevronDownIcon style={{ width: 12, height: 12 }} /> : <ChevronRightIcon style={{ width: 12, height: 12 }} />}
        {toolCalls.length} tool call{toolCalls.length !== 1 ? 's' : ''}
      </button>
      {expanded && (
        <div style={{ marginTop: SPACING[8] }}>
          {toolCalls.map((tc, i) => (
            <div key={i} style={{
              padding: SPACING[8],
              backgroundColor: 'rgba(0,0,0,0.05)',
              borderRadius: RADIUS,
              marginBottom: SPACING[4],
              fontFamily: 'monospace',
              fontSize: TYPOGRAPHY.body.sizes.small,
            }}>
              <strong>{tc.tool_name}</strong>
              <pre style={{ margin: `${SPACING[4]} 0 0`, whiteSpace: 'pre-wrap', fontSize: TYPOGRAPHY.body.sizes.tiny }}>
                {JSON.stringify(tc.tool_input, null, 2)}
              </pre>
              {toolResults[i] && (
                <details style={{ marginTop: SPACING[4] }}>
                  <summary style={{ cursor: 'pointer', color: COLORS.text.tertiary }}>Result</summary>
                  <pre style={{ whiteSpace: 'pre-wrap', fontSize: TYPOGRAPHY.body.sizes.tiny, maxHeight: 200, overflow: 'auto' }}>
                    {typeof toolResults[i].result === 'string'
                      ? (() => { try { return JSON.stringify(JSON.parse(toolResults[i].result), null, 2); } catch { return toolResults[i].result; } })()
                      : JSON.stringify(toolResults[i].result, null, 2)}
                  </pre>
                </details>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const ConfirmationCard: React.FC<{ toolCall: ToolCall; onConfirm: (tc: ToolCall) => void; onReject: () => void }> = ({ toolCall, onConfirm, onReject }) => (
  <Panel style={{ padding: SPACING[16], marginBottom: SPACING[8], border: `2px solid ${COLORS.status.warning}` }}>
    <div style={{ fontFamily: TYPOGRAPHY.body.fontFamily, fontSize: TYPOGRAPHY.body.sizes.default, marginBottom: SPACING[8] }}>
      <strong>Confirm write operation:</strong> {toolCall.tool_name}
    </div>
    <pre style={{
      fontFamily: 'monospace', fontSize: TYPOGRAPHY.body.sizes.small,
      backgroundColor: 'rgba(0,0,0,0.05)', padding: SPACING[8], borderRadius: RADIUS,
      whiteSpace: 'pre-wrap', marginBottom: SPACING[12],
    }}>
      {JSON.stringify(toolCall.tool_input, null, 2)}
    </pre>
    <div style={{ display: 'flex', gap: SPACING[8] }}>
      <Button onClick={() => onConfirm(toolCall)} style={{ display: 'flex', alignItems: 'center', gap: SPACING[4] }}>
        <CheckIcon style={{ width: 14, height: 14 }} /> Confirm
      </Button>
      <Button onClick={onReject} style={{ display: 'flex', alignItems: 'center', gap: SPACING[4], backgroundColor: COLORS.text.tertiary }}>
        <XMarkIcon style={{ width: 14, height: 14 }} /> Cancel
      </Button>
    </div>
  </Panel>
);

const BatchConfirmationCard: React.FC<{ toolCalls: ToolCall[]; onConfirmAll: () => void; onReject: () => void }> = ({ toolCalls, onConfirmAll, onReject }) => {
  const [expanded, setExpanded] = useState(false);

  // Group by tool name
  const grouped = toolCalls.reduce((acc, tc) => {
    acc[tc.tool_name] = (acc[tc.tool_name] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  return (
    <Panel style={{ padding: SPACING[16], marginBottom: SPACING[8], border: `2px solid ${COLORS.status.warning}` }}>
      <div style={{ fontFamily: TYPOGRAPHY.body.fontFamily, fontSize: TYPOGRAPHY.body.sizes.default, marginBottom: SPACING[12] }}>
        <strong>Confirm {toolCalls.length} write operations:</strong>
      </div>
      <div style={{ marginBottom: SPACING[12] }}>
        {Object.entries(grouped).map(([name, count]) => (
          <div key={name} style={{
            display: 'inline-block',
            backgroundColor: 'rgba(0,0,0,0.05)',
            padding: `${SPACING[4]} ${SPACING[8]}`,
            borderRadius: RADIUS,
            marginRight: SPACING[8],
            marginBottom: SPACING[4],
            fontFamily: TYPOGRAPHY.body.fontFamily,
            fontSize: TYPOGRAPHY.body.sizes.small,
          }}>
            {name} × {count}
          </div>
        ))}
      </div>
      <button
        onClick={() => setExpanded(!expanded)}
        style={{
          background: 'none', border: 'none', cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: SPACING[4],
          color: COLORS.text.tertiary, fontFamily: TYPOGRAPHY.body.fontFamily,
          fontSize: TYPOGRAPHY.body.sizes.small, padding: 0, marginBottom: SPACING[12],
        }}
      >
        {expanded ? <ChevronDownIcon style={{ width: 12, height: 12 }} /> : <ChevronRightIcon style={{ width: 12, height: 12 }} />}
        {expanded ? 'Hide details' : 'Show details'}
      </button>
      {expanded && (
        <div style={{
          maxHeight: 300, overflowY: 'auto', marginBottom: SPACING[12],
          backgroundColor: 'rgba(0,0,0,0.03)', borderRadius: RADIUS, padding: SPACING[8],
        }}>
          {toolCalls.map((tc, i) => (
            <div key={tc.tool_use_id} style={{
              padding: SPACING[8],
              borderBottom: i < toolCalls.length - 1 ? `1px solid ${COLORS.border.default}` : 'none',
            }}>
              <div style={{ fontWeight: 500, fontSize: TYPOGRAPHY.body.sizes.small }}>{tc.tool_name}</div>
              <pre style={{
                fontFamily: 'monospace', fontSize: TYPOGRAPHY.body.sizes.tiny,
                margin: `${SPACING[4]} 0 0`, whiteSpace: 'pre-wrap',
              }}>
                {JSON.stringify(tc.tool_input, null, 2)}
              </pre>
            </div>
          ))}
        </div>
      )}
      <div style={{ display: 'flex', gap: SPACING[8] }}>
        <Button onClick={onConfirmAll} style={{ display: 'flex', alignItems: 'center', gap: SPACING[4] }}>
          <CheckIcon style={{ width: 14, height: 14 }} /> Confirm All ({toolCalls.length})
        </Button>
        <Button onClick={onReject} style={{ display: 'flex', alignItems: 'center', gap: SPACING[4], backgroundColor: COLORS.text.tertiary }}>
          <XMarkIcon style={{ width: 14, height: 14 }} /> Cancel
        </Button>
      </div>
    </Panel>
  );
};

export default ChatPage;
